import Phaser from 'phaser';
import { MOON, SOL, type Planet } from '../../content/planets';
import { suitStats } from '../../meta/upgrades';
import { TRANSIT } from '../../sim/tuning';
import { TransitSim, type TransitEvent } from '../../sim/transit';
import { Sfx } from '../audio/Sfx';
import { FixedStepper } from '../fixedStep';
import { TransitControls } from '../input/TransitControls';
import { paintSheet } from '../render/art';
import {
  PLANET_LOOKS,
  paintNebula,
  paintPlanet,
  paintRock,
  paintStarfield,
} from '../render/spaceArt';
import {
  BOLT_SHEET,
  CELL_SHEET,
  LASER_SHEET,
  RAIDER_SHEET,
  SHARD_SHEET,
  SHIP_SHEET,
  type SpriteSheet,
} from '../render/sprites';
import { hexNumber } from '../render/palette';
import { newSeed, nextRun, type RunState } from '../run';
import { saveStore } from '../storage';
import { Hud } from '../ui/Hud';
import { PauseMenu } from '../ui/PauseMenu';
import { CELL_W, PIXEL_FONT } from '../ui/pixelFont';

const ROCK_VARIANTS = 3;
const PLANET_R = 56;

/** Star layers, back to front: scroll speed (px per tick) is the depth. */
const STARS = [
  { key: 'stars-far', width: 512, count: 80, salt: 1, streak: 0, speed: 0.5 },
  { key: 'stars-mid', width: 512, count: 45, salt: 2, streak: 0, speed: 1.6 },
  { key: 'stars-near', width: 640, count: 14, salt: 3, streak: 12, speed: 7 },
] as const;

/** Draw order. The planet sits behind the nearer stars. */
const ORDER = {
  nebula: 0,
  starsFar: 1,
  planet: 2,
  starsMid: 3,
  starsNear: 4,
  things: 10,
  ship: 12,
};

type Thing = Phaser.GameObjects.Sprite;

/**
 * Light-speed transit to the run's next planet (GDD: Universe and level design). Steps the
 * transit sim and draws it: a deep starfield streaming past, the destination planet growing on
 * the right, rocks, raiders, Starlight crystals and material shards. Arriving starts the planet's
 * first room; losing the last plate rewinds to the lab. Each leg of a run has its own waves.
 */
export class TransitScene extends Phaser.Scene {
  sim!: TransitSim;
  run!: RunState;
  /** Where this leg of the run is heading. */
  private planet: Planet = MOON;
  private hud!: Hud;
  private pause!: PauseMenu;
  private sfx!: Sfx;
  private controls!: TransitControls;
  private ship!: Thing;
  private planetImage!: Phaser.GameObjects.Image;
  private layers: { sprite: Phaser.GameObjects.TileSprite; speed: number }[] = [];
  private nebula!: Phaser.GameObjects.TileSprite;
  private readonly sprites = new Map<number, Thing>();
  private readonly stepper = new FixedStepper();
  private leaving = false;
  private prevX = 0;
  private prevY = 0;

  constructor() {
    super('Transit');
  }

  create(data: Partial<RunState>): void {
    const params = new URLSearchParams(window.location.search);
    this.run = {
      seed: data.seed ?? params.get('seed') ?? newSeed(),
      planet: data.planet ?? 0,
      room: 0,
      rewinds: data.rewinds ?? 0,
      carry: data.carry,
    };
    this.planet = SOL[this.run.planet] ?? MOON;
    this.sim = new TransitSim(
      this.scale.width,
      `${this.run.seed}/to:${this.planet.id}`,
      this.run.carry,
      suitStats(saveStore().save.upgrades),
    );
    this.sim.rewinds = this.run.rewinds;
    this.leaving = false;
    this.layers = [];
    this.sprites.clear();
    this.stepper.reset();

    this.paintTextures();
    const w = this.scale.width;
    const h = this.scale.height;
    this.nebula = this.add.tileSprite(0, 0, w, h, 'nebula').setOrigin(0).setDepth(ORDER.nebula);
    const depths = [ORDER.starsFar, ORDER.starsMid, ORDER.starsNear];
    STARS.forEach((layer, i) => {
      const sprite = this.add
        .tileSprite(0, 0, w, h, layer.key)
        .setOrigin(0)
        .setDepth(depths[i] ?? ORDER.starsFar);
      this.layers.push({ sprite, speed: layer.speed });
    });
    this.planetImage = this.add.image(0, h / 2, 'planet').setDepth(ORDER.planet);
    this.ship = this.add.sprite(0, 0, 'ship', 0).setDepth(ORDER.ship);
    this.capture();

    this.hud = new Hud(this);
    this.hud.setRoom(`TO ${this.planet.name}`, this.run.seed);
    this.hud.setDestination(hexNumber(PLANET_LOOKS[this.planet.id].high));
    this.sfx = new Sfx(this);
    this.sfx.stopAmbience();
    this.controls = new TransitControls(
      this,
      () => this.sim.ship,
      () => ({ width: this.sim.width, height: TRANSIT.height }),
    );
    this.pause = new PauseMenu(this, {
      onRewind: () => {
        if (!this.leaving) this.rewindToLab(this.sim.rewinds + 1);
      },
      onChange: (paused) => this.controls.setEnabled(!paused),
    });
    this.hud.flash('LIGHT SPEED');
    this.showHint();
    this.cameras.main.fadeIn(300, 7, 5, 26);

    const startAudio = () => this.sfx.startHum();
    if (this.sound.locked) {
      this.input.once('pointerdown', startAudio);
      this.input.keyboard?.once('keydown', startAudio);
    } else {
      startAudio();
    }
    const onResize = (size: Phaser.Structs.Size) => {
      this.sim.width = size.width;
      this.nebula.setSize(size.width, size.height);
      for (const l of this.layers) l.sprite.setSize(size.width, size.height);
      this.pause.resize();
    };
    this.scale.on('resize', onResize);
    this.events.once('shutdown', () => {
      this.scale.off('resize', onResize);
      this.sfx.stopAll();
    });
  }

  override update(_time: number, delta: number): void {
    if (!this.leaving && !this.pause.paused) {
      const steps = this.stepper.advance(delta);
      for (let i = 0; i < steps && !this.leaving; i++) {
        this.capture();
        this.react(this.sim.step(this.controls.sample()));
      }
    }
    this.draw(delta / 1000);
    const s = this.sim.ship;
    this.hud.update(
      {
        armor: s.armor,
        armorMax: this.sim.suit.armorMax,
        starlight: s.starlight,
        starlightMax: this.sim.suit.starlightMax,
        materials: saveStore().save.materials,
        lane: null,
        rewinds: this.sim.rewinds,
        boss: null,
        progress: this.sim.progress,
        x: s.x,
      },
      this.game.loop.actualFps,
    );
  }

  private capture(): void {
    this.prevX = this.sim.ship.x;
    this.prevY = this.sim.ship.y;
  }

  private paintTextures(): void {
    const canvas = (
      key: string,
      w: number,
      h: number,
      paint: (c: CanvasRenderingContext2D) => void,
    ) => {
      if (this.textures.exists(key)) this.textures.remove(key);
      const tex = this.textures.createCanvas(key, w, h);
      if (!tex) throw new Error(`Could not create texture ${key}`);
      paint(tex.context);
      tex.refresh();
      return tex;
    };
    const sheet = (key: string, s: SpriteSheet) => {
      const tex = canvas(key, s.width * s.frames.length, s.height, (c) => paintSheet(c, s));
      s.frames.forEach((_, i) => tex.add(i, 0, i * s.width, 0, s.width, s.height));
    };
    const h = TRANSIT.height;
    canvas('nebula', 512, h, (c) => paintNebula(c, 512, h));
    for (const l of STARS) {
      canvas(l.key, l.width, h, (c) => paintStarfield(c, l.width, h, l.count, l.salt, l.streak));
    }
    canvas('planet', PLANET_R * 2 + 6, PLANET_R * 2 + 6, (c) =>
      paintPlanet(c, PLANET_R, PLANET_LOOKS[this.planet.id]),
    );
    for (let i = 0; i < ROCK_VARIANTS; i++) {
      for (const [size, r] of [
        ['big', TRANSIT.bigRockRadius],
        ['small', TRANSIT.smallRockRadius],
      ] as const) {
        canvas(`rock-${size}-${i}`, r * 2 + 2, r * 2 + 2, (c) => paintRock(c, r, i * 7 + r));
      }
    }
    sheet('ship', SHIP_SHEET);
    sheet('raider', RAIDER_SHEET);
    sheet('t-cell', CELL_SHEET);
    sheet('t-shard', SHARD_SHEET);
    sheet('t-laser', LASER_SHEET);
    sheet('t-bolt', BOLT_SHEET);
  }

  private react(events: readonly TransitEvent[]): void {
    this.sfx.playTransit(events);
    for (const e of events) {
      if (e.type === 'rockBroken') {
        this.burst(e.x, e.y, '#a08a78', e.size === 'big' ? 12 : 8);
      } else if (e.type === 'raiderKilled') {
        this.burst(e.x, e.y, '#ff3a7a', 10);
      } else if (e.type === 'pickup') {
        this.burst(this.sim.ship.x, this.sim.ship.y, '#ffd23f', 8);
      } else if (e.type === 'shard') {
        saveStore().update((s) => {
          s.materials += 1;
        });
        this.burst(this.sim.ship.x, this.sim.ship.y, '#e8c8ff', 10);
      } else if (e.type === 'hurt') {
        this.cameras.main.shake(140, 0.008);
      } else if (e.type === 'rewind') {
        this.rewindToLab(this.sim.rewinds);
      } else if (e.type === 'arrived') {
        this.hud.flash(this.planet.name);
        const s = this.sim.ship;
        const next: RunState = {
          ...this.run,
          room: 0,
          rewinds: this.sim.rewinds,
          // Mods ride along in the hold: the transit has no use for them.
          carry: { starlight: s.starlight, armor: s.armor, mods: this.run.carry?.mods ?? [] },
        };
        this.leave(() => this.scene.start('Play', next), 900);
      }
    }
  }

  /** Back through time to the lab: after the last plate, or giving up from the pause menu. */
  private rewindToLab(rewinds: number): void {
    saveStore().update((s) => {
      s.stats.rewinds += 1;
    });
    this.hud.flash('REWIND');
    this.cameras.main.flash(400, 255, 255, 255);
    this.leave(() => this.scene.start('Lab', nextRun(rewinds)), 900);
  }

  private leave(go: () => void, delayMs: number): void {
    if (this.leaving) return;
    this.leaving = true;
    this.time.delayedCall(delayMs, () => {
      this.cameras.main.fadeOut(300, 7, 5, 26);
      this.cameras.main.once('camerafadeoutcomplete', go);
    });
  }

  private draw(dt: number): void {
    const w = this.scale.width;
    const h = this.scale.height;
    // Arriving: the stars slow down as the planet fills the view.
    const brake = this.sim.arrived ? 0.25 : 1;
    this.nebula.tilePositionX += 0.2 * dt * 60 * brake;
    for (const l of this.layers) l.sprite.tilePositionX += l.speed * dt * 60 * brake;

    const t = this.sim.progress;
    this.planetImage.setScale(0.3 + t * t * 1.5);
    this.planetImage.setPosition(Math.round(w - 50 + (1 - t) * 40), Math.round(h / 2));

    const a = this.stepper.alpha;
    const s = this.sim.ship;
    this.ship.setPosition(
      Math.round(this.prevX + (s.x - this.prevX) * a),
      Math.round(this.prevY + (s.y - this.prevY) * a),
    );
    this.ship.setFrame(Math.floor(this.sim.tick / 3) % 2);
    this.ship.setVisible(s.invuln === 0 || Math.floor(s.invuln / 4) % 2 === 0);

    const seen = new Set<number>();
    const tick = this.sim.tick;
    for (const r of this.sim.rocks) {
      const sprite = this.thing(r.id, seen, `rock-${r.size}-${r.id % ROCK_VARIANTS}`);
      sprite.setPosition(Math.round(r.x), Math.round(r.y));
      sprite.setRotation(tick * (((r.id % 7) - 3) * 0.01));
      this.flash(sprite, r.hitFlash > 0);
    }
    for (const r of this.sim.raiders) {
      const sprite = this.thing(r.id, seen, 'raider');
      sprite.setPosition(Math.round(r.x), Math.round(r.y));
      sprite.setFrame(Math.floor(tick / 6) % 2);
      this.flash(sprite, r.hitFlash > 0);
    }
    for (const c of this.sim.crystals) {
      const sprite = this.thing(c.id, seen, c.kind === 'shard' ? 't-shard' : 't-cell');
      sprite.setPosition(Math.round(c.x), Math.round(c.y));
      sprite.setFrame(Math.floor(tick / 10 + c.id) % 2);
    }
    for (const shot of this.sim.shots) {
      const sprite = this.thing(shot.id, seen, shot.hostile ? 't-bolt' : 't-laser');
      sprite.setPosition(Math.round(shot.x), Math.round(shot.y));
      if (shot.hostile) sprite.setFrame(Math.floor(tick / 4) % 2);
    }
    for (const [id, sprite] of this.sprites) {
      if (!seen.has(id)) {
        sprite.destroy();
        this.sprites.delete(id);
      }
    }
  }

  private thing(id: number, seen: Set<number>, texture: string): Thing {
    seen.add(id);
    let sprite = this.sprites.get(id);
    if (!sprite) {
      sprite = this.add.sprite(0, 0, texture, 0).setDepth(ORDER.things);
      this.sprites.set(id, sprite);
    }
    return sprite;
  }

  private flash(sprite: Thing, on: boolean): void {
    if (on) sprite.setTint(0xffffff).setTintMode(Phaser.TintModes.FILL);
    else sprite.clearTint();
  }

  /** Pixels flying out of a point. */
  private burst(x: number, y: number, color: string, reach: number): void {
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      const bit = this.add.rectangle(x, y, 2, 2, hexNumber(color)).setDepth(ORDER.things + 1);
      this.tweens.add({
        targets: bit,
        x: x + Math.cos(angle) * reach,
        y: y + Math.sin(angle) * reach,
        alpha: 0,
        duration: 300,
        ease: 'Cubic.easeOut',
        onComplete: () => bit.destroy(),
      });
    }
  }

  /** How to steer, until the first rewind: drag on touch screens, keys on a desktop. */
  private showHint(): void {
    if (this.run.rewinds > 0) return;
    const touch = this.sys.game.device.input.touch;
    const hint = this.add
      .bitmapText(
        Math.round(this.scale.width / 2),
        this.scale.height - 26,
        PIXEL_FONT,
        touch ? 'DRAG ANYWHERE TO STEER' : 'ARROWS OR WASD TO STEER',
        CELL_W,
      )
      .setOrigin(0.5)
      .setDepth(950);
    this.tweens.add({ targets: hint, alpha: 0, delay: 5000, duration: 1200 });
  }
}
